import { useState, useMemo } from 'react';
import { Compass, MapPin, ChevronRight, Droplets, Zap, Coffee, Castle, Trees, Waves, Mountain } from 'lucide-react';

const EXPLORE_CATEGORIES = [
  { value: 'water', label: 'Water Points', icon: Droplets, color: '#3b82f6' },
  { value: 'charging', label: 'Charging', icon: Zap, color: '#f59e0b' },
  { value: 'food', label: 'Food & Drink', icon: Coffee, color: '#22c55e' },
  { value: 'petrol', label: 'Petrol Stations', icon: MapPin, color: '#ef4444' },
  { value: 'ev_charging', label: 'EV Charging', icon: Zap, color: '#06b6d4' },
  { value: 'community', label: 'Community', icon: Trees, color: '#a855f7' },
  { value: 'shelter', label: 'Shelters', icon: Castle, color: '#6366f1' },
  { value: 'medical', label: 'Medical', icon: Mountain, color: '#ec4899' },
];

const RADII = [1, 2, 5, 10, 20, 50];

function getDistance(lat1, lng1, lat2, lng2) {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export const ExploreMode = ({ userLocation, resources, onNavigate, c1, c2 }) => {
  const [radius, setRadius] = useState(5);
  const [activeCategory, setActiveCategory] = useState(null);

  const nearbyByCategory = useMemo(() => {
    if (!userLocation || !resources?.length) return {};
    const result = {};
    for (const cat of EXPLORE_CATEGORIES) {
      const matching = resources
        .filter(r => {
          if (r.category !== cat.value) return false;
          const dist = getDistance(userLocation.lat, userLocation.lng, r.lat, r.lng);
          return dist <= radius;
        })
        .sort((a, b) => {
          const dA = getDistance(userLocation.lat, userLocation.lng, a.lat, a.lng);
          const dB = getDistance(userLocation.lat, userLocation.lng, b.lat, b.lng);
          return dA - dB;
        })
        .slice(0, 10);
      if (matching.length > 0) result[cat.value] = matching;
    }
    return result;
  }, [userLocation, resources, radius]);

  const totalNearby = Object.values(nearbyByCategory).reduce((s, arr) => s + arr.length, 0);

  return (
    <div className="space-y-3" data-testid="explore-mode">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Compass className="w-4 h-4" style={{ color: c1 }} />
          <span className="text-sm font-semibold text-foreground">Explore Nearby</span>
          <span className="text-[10px] text-muted-foreground">{totalNearby} found</span>
        </div>
        <div className="flex gap-1">
          {RADII.map(r => (
            <button
              key={r}
              onClick={() => setRadius(r)}
              className={`px-2 py-0.5 rounded text-[10px] font-medium transition-colors ${
                radius === r ? 'text-white' : 'bg-muted/30 text-muted-foreground hover:text-foreground'
              }`}
              style={radius === r ? { backgroundColor: c1 } : {}}
              data-testid={`explore-radius-${r}`}
            >
              {r}km
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-4 gap-1.5">
        {EXPLORE_CATEGORIES.map(cat => {
          const count = nearbyByCategory[cat.value]?.length || 0;
          const Icon = cat.icon;
          const active = activeCategory === cat.value;
          return (
            <button
              key={cat.value}
              onClick={() => setActiveCategory(active ? null : cat.value)}
              className={`flex flex-col items-center gap-1 py-2 rounded-lg text-center transition-colors ${
                active ? 'bg-opacity-20' : count === 0 ? 'opacity-30' : 'hover:bg-muted/20'
              }`}
              style={active ? { backgroundColor: `${cat.color}20`, borderColor: cat.color, border: '1px solid' } : {}}
              disabled={count === 0}
              data-testid={`explore-cat-${cat.value}`}
            >
              <Icon className="w-4 h-4" style={{ color: cat.color }} />
              <span className="text-[9px] font-medium text-foreground/70 leading-tight">{cat.label}</span>
              <span className="text-[9px] text-muted-foreground">{count}</span>
            </button>
          );
        })}
      </div>

      {activeCategory && nearbyByCategory[activeCategory] && (
        <div className="space-y-1 max-h-48 overflow-y-auto">
          {nearbyByCategory[activeCategory].map((r, i) => {
            const dist = getDistance(userLocation.lat, userLocation.lng, r.lat, r.lng);
            return (
              <button
                key={r.resource_id || i}
                onClick={() => onNavigate?.({ lat: r.lat, lng: r.lng, name: r.name })}
                className="w-full flex items-center gap-2 px-2.5 py-2 rounded-lg bg-muted/10 hover:bg-muted/30 transition-colors text-left"
                data-testid={`explore-item-${i}`}
              >
                <MapPin className="w-3.5 h-3.5 flex-shrink-0" style={{ color: EXPLORE_CATEGORIES.find(c => c.value === activeCategory)?.color }} />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-foreground truncate">{r.name}</p>
                  {r.address && <p className="text-[10px] text-muted-foreground truncate">{r.address}</p>}
                </div>
                <div className="flex items-center gap-1 text-[10px] text-muted-foreground flex-shrink-0">
                  <span>{dist < 1 ? `${Math.round(dist * 1000)}m` : `${dist.toFixed(1)}km`}</span>
                  <ChevronRight className="w-3 h-3" />
                </div>
              </button>
            );
          })}
        </div>
      )}

      {!userLocation && (
        <p className="text-xs text-muted-foreground text-center py-4">Enable location to explore nearby resources</p>
      )}
    </div>
  );
};
